<template>
  <div class="competitor-card bg-white rounded-2xl p-6 border border-slate-200 shadow-sm relative overflow-hidden">
    <div class="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-50 to-purple-50 rounded-bl-full -mr-8 -mt-8 opacity-50"></div>
    
    <div class="relative z-10">
      <div class="flex justify-between items-start mb-4">
        <div>
          <div class="flex items-center gap-2 mb-1">
            <h3 class="text-xl font-bold text-slate-900">{{ comp.name }}</h3>
            <span :class="['px-2 py-0.5 rounded text-xs font-bold', comp.relevance === 'High' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700']">
              {{ comp.relevance }}
            </span>
          </div>
          <a :href="'https://' + comp.website" target="_blank" class="text-sm text-blue-600 hover:underline flex items-center gap-1">
            {{ comp.website }}
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
            </svg>
          </a>
        </div>
        <div class="price-tag text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg">
          {{ comp.pricing || 'Custom' }}
        </div>
      </div>

      <div class="mb-4">
        <div class="flex flex-wrap gap-2 mb-3">
          <span v-for="feature in comp.features" :key="feature" class="px-2.5 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-md border border-slate-200">
            {{ feature }}
          </span>
        </div>
        <p class="text-slate-600 text-sm leading-relaxed">{{ comp.summary }}</p>
      </div>

      <div class="flex items-center justify-between pt-4 border-t border-slate-100">
        <div class="flex items-center gap-4 text-xs text-slate-500">
          <span class="flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full" :class="comp.telephony ? 'bg-green-500' : 'bg-gray-300'"></span>
            Telephony
          </span>
          <span class="flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full" :class="comp.scheduling ? 'bg-green-500' : 'bg-gray-300'"></span>
            Scheduling
          </span>
          <span class="flex items-center gap-1.5">
            <span class="w-2 h-2 rounded-full" :class="comp.api ? 'bg-green-500' : 'bg-gray-300'"></span>
            API Access
          </span>
        </div>
        <button 
          @click="toggleDetails"
          class="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          {{ isExpanded ? 'Less details' : 'More details' }}
          <svg class="w-3 h-3 transition-transform" :class="{ 'rotate-180': isExpanded }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </button>
      </div>

      <div v-if="isExpanded" class="mt-4 space-y-4">
        <!-- Pricing Details -->
        <div class="p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div class="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Pricing Details
          </div>
          <p class="text-xs text-slate-600 leading-relaxed">{{ comp.pricingDetails }}</p>
        </div>

        <!-- Feature Comparison -->
        <div v-if="comp.comparison" class="grid md:grid-cols-3 gap-3">
          <!-- Similar Features -->
          <div class="p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div class="text-xs font-bold text-blue-800 mb-2 flex items-center gap-1">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
              </svg>
              Similar Features
            </div>
            <ul class="space-y-1">
              <li v-for="feature in comp.comparison.similar" :key="feature" class="text-xs text-blue-700 flex items-start gap-1">
                <span class="text-blue-400 mt-0.5">•</span>
                <span>{{ feature }}</span>
              </li>
            </ul>
          </div>

          <!-- Extra Features (Competitor has) -->
          <div class="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
            <div class="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              Their Advantage
            </div>
            <ul class="space-y-1">
              <li v-for="feature in comp.comparison.extra" :key="feature" class="text-xs text-emerald-700 flex items-start gap-1">
                <span class="text-emerald-400 mt-0.5">+</span>
                <span>{{ feature }}</span>
              </li>
            </ul>
          </div>

          <!-- Missing Features (VoiceHub has) -->
          <div class="p-3 bg-amber-50 rounded-lg border border-amber-100">
            <div class="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1">
              <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              VoiceHub Advantage
            </div>
            <ul class="space-y-1">
              <li v-for="feature in comp.comparison.missing" :key="feature" class="text-xs text-amber-700 flex items-start gap-1">
                <span class="text-amber-400 mt-0.5">★</span>
                <span>{{ feature }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';

const props = defineProps({
  comp: Object
});

const isExpanded = ref(false);

const toggleDetails = () => {
  isExpanded.value = !isExpanded.value;
};
</script>
